import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CONTROLLER_CONFIG, CONTROLLER_ORDER } from '@/lib/controllerConfig';
import { IControllerType } from '@/redux/post/post.d';

export interface ApplyItem {
  id: string;
  fileName: string;
}

export function C2ApplyMultiSelect({
  candidates,
  selected,
  onChange,
  onComplete,
}: {
  candidates: ApplyItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
  onComplete: () => void;
}) {
  const getControllerFromFileName = (
    fileName: string
  ): IControllerType | null => {
    const prefixMatch = CONTROLLER_ORDER.find((controller) =>
      fileName.toLowerCase().startsWith(`${controller.toLowerCase()}_`)
    );
    if (prefixMatch) return prefixMatch as IControllerType;
    const fileExtension = fileName
      .toLowerCase()
      .substring(fileName.lastIndexOf('.'));
    const extensionMatch = CONTROLLER_ORDER.find((controller) => {
      const config = CONTROLLER_CONFIG[controller as IControllerType];
      return config.allowedFileTypes.includes(fileExtension);
    });
    return (extensionMatch as IControllerType) || null;
  };

  const candidatesByController: Record<string, ApplyItem[]> = candidates.reduce(
    (acc, item) => {
      const controller =
        getControllerFromFileName(item.fileName) || IControllerType.OTHER;
      if (!acc[controller]) acc[controller] = [];
      acc[controller].push(item);
      return acc;
    },
    {} as Record<string, ApplyItem[]>
  );

  const presentControllers = Object.keys(candidatesByController).sort(
    (a, b) =>
      CONTROLLER_ORDER.indexOf(a as IControllerType) -
      CONTROLLER_ORDER.indexOf(b as IControllerType)
  );

  const handleToggleItem = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
      return;
    }
    onChange([...selected, id]);
  };

  const handleToggleAllInController = (controller: string) => {
    const ids = candidatesByController[controller].map((c) => c.id);
    const allSelected = ids.every((id) => selected.includes(id));
    if (allSelected) {
      onChange(selected.filter((id) => !ids.includes(id)));
      return;
    }
    const next = new Set<string>([...selected, ...ids]);
    onChange(Array.from(next));
  };

  return (
    <div className="h-full flex flex-col bg-white p-4 rounded-lg shadow-sm border border-[#D6D6D6]">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-medium">
          Select the Gcode files that share the same tool data
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4">
          {candidates.length === 0 && (
            <div className="text-sm text-muted-foreground">
              No other unmapped files
            </div>
          )}
          {presentControllers.map((controller) => {
            const items = candidatesByController[controller];
            const ids = items.map((i) => i.id);
            const numSelected = ids.filter((id) =>
              selected.includes(id)
            ).length;
            const allSelected = numSelected === ids.length && ids.length > 0;
            const Icon = CONTROLLER_CONFIG[controller as IControllerType]?.icon;
            const controllerName =
              CONTROLLER_CONFIG[controller as IControllerType]?.name ||
              controller;
            return (
              <div key={controller} className="border rounded-md">
                <div className="flex items-center justify-between px-4 py-3 rounded-t-md">
                  <div className="flex items-center gap-3">
                    {Icon ? <Icon className="h-5 w-5" /> : null}
                    <div className="text-sm font-medium">{controllerName}</div>
                    <div className="text-xs font-semibold bg-neutral-800 text-white px-2 py-0.5 rounded-full">
                      {ids.length}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {numSelected} of {ids.length} selected
                    </span>
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={() =>
                        handleToggleAllInController(controller)
                      }
                    />
                  </div>
                </div>
                <div className="p-2 space-y-1">
                  {items.map((item) => {
                    const checked = selected.includes(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`w-full text-left px-4 py-2 rounded-md hover:bg-muted/80 flex items-center gap-3 ${checked ? 'bg-muted' : ''}`}
                        onClick={() => handleToggleItem(item.id)}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => handleToggleItem(item.id)}
                        />
                        <span className="text-sm">{item.fileName}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="pt-3">
        <Button className="w-full" onClick={onComplete}>
          Complete
        </Button>
      </div>
    </div>
  );
}

export default C2ApplyMultiSelect;
